/**
 * The thread's rendering decisions, as pure functions.
 *
 * ┌─ ⛔ WHY THESE ARE NOT IN THE COMPONENT ─────────────────────────────────────┐
 * │ Every one of them is a DECISION over data - which bubble starts a run, what │
 * │ a media item says when there are no bytes, what a quote shows when its      │
 * │ target is gone. Decisions are exhaustively checkable in microseconds by the │
 * │ server project; rendering them is React's job and needs a DOM.              │
 * │                                                                            │
 * │ Same split `connection-presentation.ts` already makes for the banner, and   │
 * │ `tests/web/README.md` records why: *"It is a decision about state, and it   │
 * │ is exhaustively checkable over every ConnectionState in microseconds.       │
 * │ Rendering it would test React."*                                            │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
import type { MediaItem } from '../../../src/whatsapp/media.js'
import type { ThreadMessage } from '../../../src/services/thread.js'

/**
 * ⛔ RUNS ARE A PROPERTY OF THE PAGE, NOT OF THE ROW, which is why this takes
 * the neighbour rather than living on the message.
 *
 * The rule: avatar and name on the FIRST bubble of a run, square upper corners
 * after. A run is consecutive messages from the same author on the same side.
 *
 * ⚠️ `sender_user_id` IS NOT ENOUGH ON ITS OWN. It is null for customer and bot
 * messages, so comparing it alone makes every inbound message "the same author"
 * as the one above it and collapses an entire conversation into one run. The
 * author identity is the PAIR (direction, sender_type, sender_user_id).
 *
 * ⚠️ AND THE PAGE ARRIVES NEWEST FIRST. The "previous" bubble visually is the
 * NEXT element in the array, which is why the caller passes `rows[i + 1]`.
 */
export function startsRun(message: ThreadMessage, previous: ThreadMessage | undefined): boolean {
  if (!previous) return true
  return (
    previous.direction !== message.direction ||
    previous.sender_type !== message.sender_type ||
    previous.sender_user_id !== message.sender_user_id
  )
}

/**
 * The author's display name, or null where there is not one to show.
 *
 * ⚠️ NULL FOR THE CUSTOMER, DELIBERATELY. Their name is on the thread header and
 * on every conversation row; repeating it above each inbound bubble is noise
 * that pushes the message itself down. Outbound messages need it because the
 * team has several people and "who replied" is the question being answered.
 */
export function authorName(message: ThreadMessage): string | null {
  if (message.direction === 'inbound') return null
  if (message.sender_type === 'bot') return 'Automated'
  const full = [message.first_name, message.last_name].filter(Boolean).join(' ').trim()
  return full === '' ? null : full
}

/**
 * ┌─ ⛔ WHAT KIND OF ROW THIS IS, WHICH THE THREAD DID NOT ASK UNTIL 2026-08-11 ┐
 * │ **INBOX scope item 9.** Measured before writing: `type` has seven values in │
 * │ the corpus and `ThreadMessage` rendered ALL of them as bubbles. A `note`    │
 * │ has `direction='internal'`, which is not `'outbound'`, so **every internal  │
 * │ note was drawn in the customer's own inbound style** - the precise thing    │
 * │ the plan's rule exists to prevent: *"a note is not part of the customer     │
 * │ conversation and should never be mistaken for one."*                        │
 * │                                                                            │
 * │ ⚠️ 70 note rows and 405 system rows were on screen wearing a customer's     │
 * │ face, and no test could see it: every thread assertion is about a bubble's  │
 * │ CONTENT, and the content was right.                                        │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ THE DISCRIMINATOR IS `type`, NOT `direction` OR `sender_type`. All three
 * correlate in today's corpus - every note is `internal`/`agent`, every system
 * row is `outbound`/`system` - and correlation is exactly what makes the wrong
 * one comfortable to pick. `messages_note_check` constrains `direction` and
 * `status` FROM `type`, so `type` is the column the database treats as the fact
 * and the other two as its consequences.
 */
export type RowKind =
  | { kind: 'message' }
  /**
   * `source` is the attribution line, and ⛔ IT IS NOT OPTIONAL FOR A NON-HUMAN
   * NOTE. The plan: *"SYSTEM-GENERATED NOTES are also yellow AND NAME THEIR
   * SOURCE, e.g. 'created by Make HTTP request'. Without the attribution an
   * automated note is indistinguishable from something a colleague wrote, which
   * is how people end up replying to a robot."*
   */
  | { kind: 'note'; attribution: string }
  /**
   * ⛔ A DELETED NOTE IS ITS OWN KIND, NOT A NOTE WITH NO TEXT. Migration
   * `0019` destroys the body and keeps the row, so without this the deleted
   * note would fall into the note arm and render as *"This note is empty"* -
   * which is a different fact about a different row and would send whoever read
   * it looking for a bug.
   */
  | { kind: 'deletedNote' }
  | { kind: 'system' }

/**
 * Who caused a system line, as a suffix, or `null` when nobody should be named.
 *
 * ┌─ ⛔ AMIR'S INSPECTION: *"System lines in the thread do not say who did it."* ┐
 * │ 2026-08-20. "Conversation closed" named no one, so an agent reading the     │
 * │ thread could not tell whether a colleague acted or the system did.          │
 * │                                                                            │
 * │ ⚠️ THE ACTOR COMES FROM THE EVENT, NOT THE MESSAGE, and it has to: the      │
 * │ authorship CHECK requires `sender_user_id IS NULL` whenever `sender_type`   │
 * │ is not `agent`, and a system row is never `agent`. `thread.ts` joins        │
 * │ `conversation_events` through `source_event_id` to resolve it.              │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ **A CUSTOMER ACTOR IS DELIBERATELY NOT NAMED, AND THAT IS THE COMMON CASE
 * RATHER THAN AN EDGE.** A reopen usually happens because the customer messaged
 * again; writing "reopened by Marta Reyes" into Marta's own conversation states
 * the obvious and reads as if somebody on the team did it.
 *
 * ⚠️ AND AN AGENT WITH NO NAME STILL GETS ATTRIBUTED. The user row can be gone;
 * "by an agent" is true and useful, and "by " with nothing after it is not.
 */
export function systemAttribution(
  message: Pick<ThreadMessage, 'actor_type' | 'actor_first_name' | 'actor_last_name'>,
): string | null {
  if (message.actor_type === 'agent') {
    const name = [message.actor_first_name, message.actor_last_name]
      .filter((p) => (p ?? '').trim() !== '')
      .join(' ')
      .trim()
    return name === '' ? 'by an agent' : `by ${name}`
  }
  /**
   * `bot` is an automation acting; `customer`, `system` and null name nobody.
   *
   * ⚠️ "an automation" AND NOT *WHICH* AUTOMATION, which is Amir's finding 12
   * and is blocked rather than skipped: nothing in this codebase writes a bot
   * message at all, so there is no automation whose identity could be recorded.
   * Registered as `automation-identity-has-no-producer` in `ops/deferrals.txt`.
   *
   * ⚠️ AND THE SYSTEM LINE ITSELF HAS NO PRODUCER since 2026-09-06, when every
   * status transition became internal. The arm stays because Amir kept the
   * CONCEPT - a system line is for something the system did that the customer is
   * affected by, and those producers are unbuilt rather than unwanted. Covered
   * as a rendering claim over a hand-built row; see
   * `system-line-has-no-producer-or-subject`.
   */
  if (message.actor_type === 'bot') return 'by an automation'
  return null
}

export function rowKind(message: ThreadMessage): RowKind {
  if (message.type === 'system') return { kind: 'system' }
  if (message.type !== 'note') return { kind: 'message' }
  /**
   * ⚠️ TESTED BEFORE THE ATTRIBUTION IS COMPUTED, and on `deleted_at` rather
   * than on an empty body. The two are both true for a deleted note - the CHECK
   * requires it - but an empty body ALONE is the "this note is empty" arm, which
   * is a real and different row.
   */
  if (message.deleted_at !== null) return { kind: 'deletedNote' }

  /**
   * ┌─ ⛔ NO ROW IN THE CORPUS REACHES THE `source` BRANCH, AND THAT IS STATED ─┐
   * │ RATHER THAN HIDDEN. All 70 notes are `sender_type='agent'` with a user    │
   * │ id. **Nothing in this system writes a note from any other actor**:        │
   * │ `createNote()` takes a user and sets `sender_type='agent'` unconditionally, │
   * │ so the automated-note case has NO PRODUCER, not merely no seed data.       │
   * │                                                                          │
   * │ ⚠️ THE BRANCH IS STILL WRITTEN, because the alternative is a note from a  │
   * │ future integration silently rendering as a colleague's - which is the     │
   * │ failure the rule names, arriving the day the producer is built. It is     │
   * │ covered by unit tests over synthetic rows and by NOTHING END TO END, and  │
   * │ `ops/deferrals.txt` carries `system-note-producer-absent`.                │
   * │                                                                          │
   * │ ⛔ THE WORDS COME FROM `metadata.source` AND THERE IS NO CONVENTION FOR   │
   * │ WRITING IT. `messages.metadata` is free-form jsonb. Reading a key nobody  │
   * │ writes yet is a guess about a future producer, so this returns null when  │
   * │ the key is absent and the render says the source is UNKNOWN rather than   │
   * │ inventing one or falling back to a person's name.                         │
   * └──────────────────────────────────────────────────────────────────────────┘
   */
  /**
   * ⛔ FOUR SENTENCES, AND THE PAIR THAT MUST NEVER COLLAPSE IS THE UNNAMED
   * ONES. A colleague we cannot name and a machine we cannot name are DIFFERENT
   * FACTS, and rendering one string for both is exactly the failure the rule
   * names - it is how somebody replies to a robot. So the actor decides the
   * sentence first and the name only fills it in.
   */
  if (message.sender_type === 'agent') {
    const who = authorName(message)
    return { kind: 'note', attribution: who === null ? 'Note from a colleague' : `Note from ${who}` }
  }
  const raw = (message.metadata as { source?: unknown } | null)?.source
  const source = typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : null
  return {
    kind: 'note',
    attribution: source === null ? 'Automated note, source not recorded' : `Note created by ${source}`,
  }
}

export type BodyRender =
  | { kind: 'text'; text: string }
  | { kind: 'template'; templateName: string }
  | { kind: 'media-only' }
  | { kind: 'unrenderable'; reason: string }

/**
 * What the bubble shows, and ⛔ THERE IS NO BRANCH THAT SHOWS NOTHING.
 *
 * ┌─ THE RULE THIS IS THE FOURTH APPLICATION OF ───────────────────────────────┐
 * │ "No empty state indistinguishable from a real one" has already dropped the │
 * │ booking line, the avatar placeholder and the list preview. Here it applies  │
 * │ to a message with no body.                                                  │
 * │                                                                            │
 * │ ⛔ §9.0 RULE 6 IS THE SHARP CASE. A template send has `body IS NULL` -      │
 * │ ALWAYS - because the text lives at Meta and we store the NAME and the       │
 * │ parameters. A component reading `body` renders an empty bubble for every    │
 * │ booking confirmation the business has ever sent, with no error anywhere.    │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
export function bodyRender(message: ThreadMessage): BodyRender {
  /**
   * ⚠️ THIS IS LOOSER THAN THE RULE IT IMPLEMENTS, AND THE SPEC ALREADY SAYS SO.
   * `schema.md:179` states rule 6 as a CONJUNCTION - "`body IS NULL` **and**
   * `type = 'template'`" - with the next row saying a null body on any other
   * type is "genuinely empty; render the type's placeholder". This asks only
   * whether the column is present, so it renders as a template any row carrying
   * one, whatever its type. `src/whatsapp/outbound.ts` asks `row.type` and
   * matches the spec; **the two sides of the system disagree about what a
   * template IS.**
   *
   * ⛔ NOT TIGHTENED IN PLACE, BECAUSE IT WOULD BE A SILENT CORPUS CHANGE: all
   * 60 seeded template rows are `type='text'`, so they would flip to the
   * empty-body placeholder - correctly, per the spec - and that is the
   * measurement proving the corpus has never held a real template row. The
   * seeder has to move with it. See
   * `template-identified-two-ways-and-the-corpus-matches-neither`.
   */
  if (message.template) {
    const name = (message.template as { name?: unknown }).name
    return {
      kind: 'template',
      // ⚠️ The stored name is an identifier like `booking_confirmation`. It is
      // shown rather than hidden: an agent seeing "Booking Confirmation" knows
      // which template went out, and showing nothing loses that entirely.
      templateName: typeof name === 'string' && name !== '' ? name : 'template',
    }
  }
  if (message.body !== null && message.body.trim() !== '') {
    return { kind: 'text', text: message.body }
  }
  // A media message with no caption is COMPLETE, not empty - the attachment is
  // the content. The bubble renders the media and no text.
  if (message.media && message.media.items.length > 0) return { kind: 'media-only' }

  /**
   * ⛔ EVERYTHING ELSE IS A REAL FAILURE AND SAYS SO. A row with no body, no
   * template and no media should not exist; if one does, the honest render is a
   * statement that something is wrong rather than a blank space an agent reads
   * as "the customer sent an empty message".
   */
  return { kind: 'unrenderable', reason: `No content (type ${message.type})` }
}

export type MediaRender = {
  /** What the bubble says. Never empty - §8.5's promise. */
  label: string
  /** `true` only when bytes exist to fetch. */
  showsContent: boolean
  /** A recoverable state still in progress. */
  pending: boolean
  /** A permanent failure. Distinct from pending for the wording and the icon. */
  failed: boolean
}

/**
 * §8.5, all six arms.
 *
 * ┌─ ⛔ `download_state` COMES FROM THE THREAD READ AND NOWHERE ELSE ───────────┐
 * │ `GET /api/media/:messageId/:index` returns a UNIFORM 404 for pending,       │
 * │ expired, too_large and not-found alike, deliberately, so an authenticated   │
 * │ user cannot probe which message ids exist. **So the endpoint cannot tell    │
 * │ this function which arm to render** - it answers 404 for four different     │
 * │ reasons. `services/thread.ts` selects `media` whole for exactly this.       │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ THE PROMISE, QUOTED SO IT CANNOT BE RE-DERIVED WRONGLY: *"Every media item
 * renders SOMETHING. There is no state in which a media message renders as
 * though it had no attachment."* The obvious implementation - show the image if
 * `stored`, else nothing - collapses four states into "the customer sent no
 * media", and an agent then answers the wrong thing while the customer is
 * looking at the photo they sent.
 */
export function mediaRender(item: MediaItem, direction: string): MediaRender {
  const noun = mediaNoun(item.mime_type)

  // ⛔ THE OUTBOUND ARM IS KEYED ON DIRECTION, NOT ON STATE. Media WE sent was
  // never downloaded from anywhere, so its `download_state` says nothing about
  // whether it can be shown.
  if (direction === 'outbound') {
    return { label: noun, showsContent: true, pending: false, failed: false }
  }

  switch (item.download_state) {
    case 'stored':
      return { label: noun, showsContent: true, pending: false, failed: false }

    case 'pending':
      // Arriving vs Retrying. One state, two arms: `error` set means it has
      // already failed at least once, which is recoverable but not healthy.
      return item.error
        ? { label: `${noun} - still fetching`, showsContent: false, pending: true, failed: false }
        : { label: `${noun} - loading`, showsContent: false, pending: true, failed: false }

    case 'too_large':
      /**
       * ⛔ OUR POLICY, NOT A FAULT. Nothing is broken and nothing changes
       * without an admin raising the limit, so the wording must not read as an
       * error or agents will report it as one.
       */
      return {
        label: `${noun} not downloaded (over the size limit${sizeSuffix(item.file_size)})`,
        showsContent: false,
        pending: false,
        failed: false,
      }

    case 'expired':
      /**
       * ⛔ UNRECOVERABLE, AND THE EVENT MUST SURVIVE. Meta's URL is gone.
       * Rendering nothing erases the fact that a customer sent a photo; a
       * spinner is a lie that never resolves.
       */
      return {
        label: `${noun} could not be retrieved (no longer available from WhatsApp)`,
        showsContent: false,
        pending: false,
        failed: true,
      }

    default:
      /**
       * ⚠️ NO WRITER PRODUCES THIS. `MediaItem.download_state` is required by
       * the type, so a descriptor without it can only come from corrupt or
       * hand-edited JSON - which is why it is NOT seeded and is a fixture case
       * instead (scope items 9-10, and the scope doc says the sixth arm is the
       * outbound one rather than this).
       *
       * ⛔ It renders as BROKEN rather than as a placeholder implying Meta still
       * holds it. A component that treated an unknown state as "loading" would
       * show a spinner forever for data that will never arrive.
       */
      return {
        label: `${noun} could not be read (unrecognised state)`,
        showsContent: false,
        pending: false,
        failed: true,
      }
  }
}

/** "Photo", "Document" - the type is known from ingest in every case. */
function mediaNoun(mime: string | null | undefined): string {
  if (!mime) return 'Attachment'
  if (mime.startsWith('image/')) return 'Photo'
  if (mime.startsWith('video/')) return 'Video'
  if (mime.startsWith('audio/')) return 'Voice message'
  return 'Document'
}

function sizeSuffix(bytes: number | null | undefined): string {
  if (bytes == null || bytes <= 0) return ''
  return `, ${Math.round(bytes / (1024 * 1024))} MB`
}

export type QuoteRender = { text: string; missing: boolean }

/**
 * The quoted message, or a statement that it is gone.
 *
 * ⚠️ THE TARGET CAN BE ABSENT AND THAT IS NORMAL. `reply_to_message_id` points
 * at a message that may predate our history entirely - a customer can quote
 * something from before the number was connected - and the thread read's LEFT
 * JOIN returns nulls for it.
 *
 * ⛔ Rendering nothing would make a reply look like an ordinary message, which
 * loses the fact that the customer was answering something specific. Same rule
 * as everywhere else: the absence is stated.
 */
export function quoteRender(message: ThreadMessage): QuoteRender | null {
  if (message.reply_to_message_id === null) return null
  if (message.quoted_body !== null && message.quoted_body.trim() !== '') {
    return { text: message.quoted_body, missing: false }
  }
  // A quoted MEDIA message has a null body and is not missing.
  if (message.quoted_type !== null && message.quoted_type !== 'text') {
    return { text: mediaNounForType(message.quoted_type), missing: false }
  }
  return { text: 'Original message unavailable', missing: true }
}

function mediaNounForType(type: string): string {
  switch (type) {
    case 'image':
      return 'Photo'
    case 'video':
      return 'Video'
    case 'audio':
      return 'Voice message'
    case 'document':
      return 'Document'
    default:
      return 'Message'
  }
}
